import { ClinicConditionSpotlight, ClinicWithConditionsAndPromo } from '../../components/Clinics/types';
import { coreApi } from '../../utils/axios';
import { PrescriberPublicProfile, PrescribingProtocol } from '../users';
import {
  GetListingOrganisationsResponse,
  AddListingOrganisationResponse,
  AddListingOrganisationRequest,
  PaginationParams,
  GetClinicsResponse,
  GetClinicResponse,
  GetFulfilmentsResponse,
  AddFulfilmentResponse,
  AddFulfilmentRequest,
  GetClinicConditionsResponse,
  CreateClinicConditionResponse,
  GetExcludedOrganisationsResponse,
  AddExcludedOrganisationRequest,
  AddExcludedOrganisationResponse,
} from './types';

export const getClinics = async (page: number = 0, limit: number = 50) => {
  const response = await coreApi.get<GetClinicsResponse>(`/clinics?page=${page}&limit=${limit}`);

  return response.data;
};

export const getClinicById = async (clinicId: string) => {
  const response = await coreApi.get<GetClinicResponse>(`/clinics/${clinicId}`);

  return response.data.data[0];
};

export const getClinicConditions = async (clinicId: string) => {
  const response = await coreApi.get<GetClinicConditionsResponse>(`/clinics/${clinicId}/conditions`);

  return response.data;
};

export const updateClinic = async (clinicId: string, clinic: Partial<ClinicWithConditionsAndPromo>) => {
  const payload = {
    ...clinic,
    fulfilment_type: clinic.fulfilment_type
  };
  const response = await coreApi.put(`/clinics/${clinicId}`, payload);
  return response.data;
}

export const postClinic = async (clinic: ClinicWithConditionsAndPromo) => {
  const response = await coreApi.post('/clinics', clinic);

  return response.data;
}

// The clinic PUT can only link an existing spotlight row; these create/delete the row itself.
export const upsertConditionSpotlight = async (
  clinicId: string,
  clinicConditionId: string,
  spotlight: ClinicConditionSpotlight,
) => {
  const response = await coreApi.put(`/clinics/${clinicId}/conditions/${clinicConditionId}/spotlight`, spotlight);
  return response.data;
};

export const deleteConditionSpotlight = async (clinicId: string, clinicConditionId: string): Promise<void> => {
  await coreApi.delete(`/clinics/${clinicId}/conditions/${clinicConditionId}/spotlight`);
};

export interface CloneClinicRequest {
  sourceClinicId: string;
  clinic?: {
    name?: string;
    internal_prescribing?: boolean;
    internal_product_charge?: boolean;
    fulfilment_type?: string;
  };
}

export const cloneClinic = async (data: CloneClinicRequest) => {
  const response = await coreApi.post('/clinics/clone', data);
  return response.data;
}

export const deleteClinic = async (clinicId: string) => {
  await coreApi.delete(`/clinics/${clinicId}`);
}

export const getListingOrganisations = async (clinicId: string, params: PaginationParams) => {
  const response = await coreApi.get<GetListingOrganisationsResponse>(`/clinics/${clinicId}/listing-organisations`, {
    params: {
      page: params.page,
      limit: params.limit
    }
  });
  return {
    data: response.data.data,
    pagination: response.data.pagination
  };
};

export const addListingOrganisation = async (clinicId: string, organisationId: string) => {
  const payload: AddListingOrganisationRequest = {
    organisation_id: organisationId,
  };
  const response = await coreApi.post<AddListingOrganisationResponse>(`/clinics/${clinicId}/listing-organisations`, payload);
  return response.data.data;
};

export const removeListingOrganisation = async (clinicId: string, organisationId: string) => {
  await coreApi.delete(`/clinics/${clinicId}/listing-organisations/${organisationId}`);
};

export const getExcludedOrganisations = async (clinicId: string, params: PaginationParams) => {
  const response = await coreApi.get<GetExcludedOrganisationsResponse>(`/clinics/${clinicId}/excluded-organisations`, {
    params: {
      page: params.page,
      limit: params.limit
    }
  });
  return {
    data: response.data.data,
    pagination: response.data.pagination
  };
};

export const addExcludedOrganisation = async (clinicId: string, organisationId: string) => {
  const payload: AddExcludedOrganisationRequest = {
    organisation_id: organisationId,
  };
  const response = await coreApi.post<AddExcludedOrganisationResponse>(`/clinics/${clinicId}/excluded-organisations`, payload);
  return response.data.data;
};

export const removeExcludedOrganisation = async (clinicId: string, organisationId: string) => {
  await coreApi.delete(`/clinics/${clinicId}/excluded-organisations/${organisationId}`);
};

export const getFulfilments = async (clinicId: string, params: PaginationParams) => {
  const response = await coreApi.get<GetFulfilmentsResponse>(`/clinics/${clinicId}/fulfilments`, {
    params: {
      page: params.page,
      limit: params.limit
    }
  });

  return {
    data: response.data.data,
    pagination: response.data.pagination
  };
};

export const addFulfilment = async (clinicId: string, body: AddFulfilmentRequest) => {
  const response = await coreApi.post<AddFulfilmentResponse>(`/clinics/${clinicId}/fulfilments`, body);
  return response.data.data;
};

export const removeFulfilment = async (clinicId: string, fulfilmentId: string) => {
  await coreApi.delete(`/clinics/${clinicId}/fulfilments/${fulfilmentId}`);
};

interface CreateConditionFromClinicRequest {
  condition_id: string;
  clinic_id: string;
}

export const addConditionFromAnotherClinic = async (request: CreateConditionFromClinicRequest) => {
  const { condition_id, ...requestBody } = request;
  const response = await coreApi.post<CreateClinicConditionResponse>(`/clinic-conditions/${condition_id}/add-from-another-clinic`, requestBody);
  return response.data.data;
};

export const removeConditionFromAnotherClinic = async (conditionId: string, clinicId: string): Promise<void> => {
  const url = `/clinic-conditions/${conditionId}/remove-from-another-clinic/${clinicId}`;
  await coreApi.delete(url);
};

export const getPrescribers = async (clinicId: string) => {
  const response = await coreApi.get(`/clinics/${clinicId}/prescribers`);
  return response.data;
};

interface CreatePrescriberRequest {
  username: string;
  forename: string;
  surname: string;
  roles: string[];
  telephone?: string;
  address?: string;
  postcode?: string;
  nmc_number?: string;
  gphc_number?: string;
  clinic_name?: string;
  registration_body?: string;
  prescribing_protocols?: PrescribingProtocol[];
  public_profile?: PrescriberPublicProfile;
  show_on_clinic?: boolean;
}

export const createPrescriber = async (clinicId: string, data: CreatePrescriberRequest) => {
  const response = await coreApi.post(`/clinics/${clinicId}/prescribers`, data);
  return response.data;
};

export const updatePrescriber = async (clinicId: string, prescriberId: string, data: {
  username: string;
  forename: string;
  surname: string;
  roles: string[];
  nmc_number?: string;
  gphc_number?: string;
  address?: string;
  postcode?: string;
  telephone?: string;
  clinic_name?: string;
  registration_body?: string;
  prescribing_protocols?: PrescribingProtocol[] | null;
  public_profile?: PrescriberPublicProfile | null;
  show_on_clinic?: boolean;
}) => {
  const response = await coreApi.put(`/clinics/${clinicId}/prescribers/${prescriberId}`, data);
  return response.data;
};

export const removePrescriber = async (clinicId: string, prescriberId: string) => {
  const response = await coreApi.delete(`/clinics/${clinicId}/prescribers/${prescriberId}`);
  return response.data;
};
